import { useCallback, useLayoutEffect, useRef, useState } from "react";

const SCROLL_BOTTOM_THRESHOLD = 50;

interface ScrollAnchor {
  id: string;
  offset: number;
  atBottom: boolean;
}

export const useScrollPreservation = <T>(
  containerRef: React.RefObject<HTMLElement>,
  activeThread: string | null,
  messages: T[]
) => {
  const savedScrollAnchor = useRef<ScrollAnchor | null>(null);
  const [skipNextScroll, setSkipNextScroll] = useState(false);

  // Helper to capture current scroll state
  const captureScrollState = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const scrollBottom = container.scrollTop + container.clientHeight;
    // Check if we're at the bottom (within threshold)
    const isAtBottom = Math.abs(container.scrollHeight - scrollBottom) < SCROLL_BOTTOM_THRESHOLD;

    if (isAtBottom) {
      savedScrollAnchor.current = { id: "", offset: 0, atBottom: true };
      return;
    }

    // Find the last visible message to anchor to (bottom-up search)
    const messages = Array.from(container.children).filter((child) => child.id.startsWith("msg-"));
    let targetMsg: HTMLElement | null = null;

    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i] as HTMLElement;
      // Find the last message that starts before the viewport ends
      if (msg.offsetTop < scrollBottom) {
        targetMsg = msg;
        break;
      }
    }

    if (targetMsg) {
      // Save offset from the bottom of the viewport
      // offset = distance from viewport bottom to element bottom
      // (Positive if element ends above viewport bottom, Negative if element cuts off)
      const elementBottom = targetMsg.offsetTop + targetMsg.offsetHeight;
      const offsetFromViewBottom = scrollBottom - elementBottom;

      savedScrollAnchor.current = {
        id: targetMsg.id,
        offset: offsetFromViewBottom,
        atBottom: false,
      };
    }
  }, [containerRef]);

  // Restore scroll position after sidebar opens (runs synchronously before paint)
  // biome-ignore lint/correctness/useExhaustiveDependencies: activeThread and messages are intentional triggers; containerRef.current is a ref
  useLayoutEffect(() => {
    const anchor = savedScrollAnchor.current;
    const container = containerRef.current;

    if (anchor && container) {
      if (anchor.atBottom) {
        container.scrollTop = container.scrollHeight;
      } else if (anchor.id) {
        const anchorEl = document.getElementById(anchor.id);
        if (anchorEl) {
          // Restore position: Keep the bottom of the element at the same distance from the viewport bottom
          // scrollTop = (elementBottom + offset) - clientHeight
          const elementBottom = anchorEl.offsetTop + anchorEl.offsetHeight;
          container.scrollTop = elementBottom + anchor.offset - container.clientHeight;
        }
      }
      savedScrollAnchor.current = null;
      // Disable smooth scrolling for the immediate next scroll update to prevent jumping
      setSkipNextScroll(true);
    }
  }, [activeThread, messages]);

  const resetSkipNextScroll = useCallback(() => {
    setSkipNextScroll(false);
  }, []);

  return {
    captureScrollState,
    skipNextScroll,
    resetSkipNextScroll,
  };
};
